import React, { useCallback, useMemo, useState } from 'react';
import useAppBarTitle from 'hooks/use-app-bar-title';
import useAdminsGet from '../../hooks/use-admins-get';
import Button from 'components/control/button/button';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import {
    EButtonSize,
    EButtonType,
} from 'components/control/button/button.types';
import { admins } from 'types';
import { Col, Row } from 'components/layout/layout';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import MaterialTable from 'components/table';
import DialogCrudAdminAdd from '../../dialogs/add';
import DialogCrudAdminDel from '../../dialogs/del';
import UnblockDialog from '../../dialogs/unblock';
import { toast } from 'react-toastify';
import messages from 'messages';
import PeopleAltOutlinedIcon from '@material-ui/icons/PeopleAltOutlined';
import adminListColumns from './admin-list.columns';
import { useRouter } from 'hooks/use-router';
import { ERoutesPrivate, ERoutesType, routes } from 'types/routes';
import { Root } from '../../index.styled';
import { EUsersRoutes } from '../../index.type';

const pageTitle = 'Admins';

const AdminList = () => {
    useAppBarTitle(pageTitle);
    const { history } = useRouter();

    const { data, refetch, isLoading } = useAdminsGet();

    const [dialogAddOpen, setDialogAddOpen] = useState<boolean>(false);
    const [dialogDelOpen, setDialogDelOpen] = useState<boolean>(false);
    const [isUnblockDialogOpen, setUnblockDialogOpen] =
        useState<boolean>(false);
    const [curAdminData, setCurAdminData] = useState<admins.TAdmins>({});

    const handleOnSuccessMutation = useCallback(() => {
        refetch();
    }, [refetch]);

    const handleOnUnblock = useCallback((rowData) => {
        setCurAdminData(rowData);
        setUnblockDialogOpen(true);
    }, []);

    const handlePermissionManagementButtonClick = () => {
        history.push(
            `${routes[ERoutesType.Private][ERoutesPrivate.Admins][0]}/${
                EUsersRoutes.PermissionManagement
            }`,
        );
    };

    const adminColumnsRendered = useMemo(() => {
        return adminListColumns({
            onUnblock: handleOnUnblock,
            ControlButtonsComponent: ({ rowData }) =>
                rowData.role !== 'superadmin' ? (
                    <Button
                        onClick={() => {
                            setCurAdminData(rowData);
                            setTimeout(() => {
                                setDialogDelOpen(true);
                            }, 0);
                        }}
                        styleType={EButtonType.Contrast}
                        size={EButtonSize.Small}
                    >
                        <DeleteForeverIcon />
                    </Button>
                ) : null,
        });
    }, []);

    return (
        <>
            <Root>
                <Row justify={'start'} gap={3}>
                    <Col>
                        <Button onClick={() => setDialogAddOpen(true)}>
                            <Col gap={0.5}>
                                <AddCircleOutlineIcon /> Create new admin
                            </Col>
                        </Button>
                        <Button onClick={handlePermissionManagementButtonClick}>
                            <Col gap={0.5}>
                                <PeopleAltOutlinedIcon /> Permission Management
                            </Col>
                        </Button>
                    </Col>
                </Row>
                <MaterialTable<admins.TAdmins>
                    data={data}
                    columns={adminColumnsRendered}
                    isLoading={isLoading || dialogAddOpen}
                />
            </Root>

            <DialogCrudAdminAdd
                isDialogOpen={dialogAddOpen}
                setIsDialogOpen={setDialogAddOpen}
                asyncStatesHandlers={{
                    onSuccess: () => {
                        handleOnSuccessMutation();
                        toast.success(
                            messages.admins.add?.onSuccess &&
                                messages.admins.add?.onSuccess(),
                        );
                    },
                    onError: (e) => {
                        toast.error(
                            messages.admins.add?.onError &&
                                messages.admins.add?.onError(e),
                        );
                    },
                }}
            />
            <DialogCrudAdminDel
                data={curAdminData}
                isDialogOpen={dialogDelOpen}
                setIsDialogOpen={setDialogDelOpen}
                asyncStatesHandlers={{
                    onSuccess: () => {
                        handleOnSuccessMutation();
                        toast.success(
                            messages.admins.del?.onSuccess &&
                                messages.admins.del?.onSuccess(),
                        );
                    },
                    onError: (e) => {
                        toast.error(
                            messages.admins.del?.onError &&
                                messages.admins.del?.onError(e),
                        );
                    },
                }}
            />
            <UnblockDialog
                data={curAdminData}
                isDialogOpen={isUnblockDialogOpen}
                setIsDialogOpen={setUnblockDialogOpen}
                asyncStatesHandlers={{
                    onSuccess: () => {
                        handleOnSuccessMutation();
                        toast.success('Successfully unblocked');
                    },
                    onError: (e) => {
                        toast.error(`Error: ${e}`);
                    },
                }}
            />
        </>
    );
};

export default AdminList;
